import { EditorContent, useEditor } from '@tiptap/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Y from 'yjs';

import { Box } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useEditorExtensions } from '../extensions/extensions';
import { useCollaborativeDocumentSync } from '../hooks/use-collaborative-document-sync';
import { useEditorReadOnly } from '../hooks/use-editor-read-only';
import { useUserColor } from '../hooks/use-user-color';
import { ActionCableProvider } from '../providers/action-cable-provider';
import { BubbleMenu } from './bubble-menu';
import { editorStyles } from './editor-styles';

/**
 * Check if the backend provided YJS collaboration state.
 * When the nightly job creates a document with only HTML content,
 * the collaboration_state will be null/undefined.
 */
function hasCollaborationState(provider: ActionCableProvider): boolean {
	// Check if the backend sent actual YJS state in the initial payload
	// If not, we should hydrate from HTML content instead
	return !!provider.initialStatePayload?.state;
}

interface CoreEditorProps {
	yjsDoc: Y.Doc;
	provider: ActionCableProvider;
	readOnly?: boolean;
	placeholder?: string;
	uploadEndpoint?: string;
	onEditorReady?: (editor: any) => void;
}

function CoreEditorComponent({ yjsDoc, provider, readOnly = false, placeholder, uploadEndpoint, onEditorReady }: CoreEditorProps) {
	const { t } = useTranslation('one_on_ones');
	const currentUser = useCurrentUser();
	const userColor = useUserColor(currentUser.id, 'hex');
	const saveDebounceTimeoutMs = 2000;

	// Track whether initial content has been loaded to prevent saving during init
	const [isReady, setIsReady] = useState(false);

	// Track whether we've already tried to hydrate from HTML fallback
	const hasHydratedFromFallback = useRef(false);

	// Handle image uploads (optional)
	const handleUploadFile = uploadEndpoint
		? async (_file: File) => {
				// TODO: Implement generic upload handler
				return null;
			}
		: undefined;

	const extensions = useEditorExtensions({
		yjsDoc,
		yjsAwareness: provider.awareness,
		provider: provider,
		currentUser: {
			id: currentUser.id,
			display_name: currentUser.display_name,
		},
		userColor,
		onUploadFile: handleUploadFile,
		placeholder: placeholder || t('editor.placeholder', { defaultValue: 'Start typing...' }),
	});

	const editor = useEditor({
		extensions,
		editable: !readOnly,
		// Important: Don't set initial content here - let YJS/Collaboration extension handle it
		// Performance optimizations per https://v2.tiptap.dev/docs/guides/performance
		immediatelyRender: true,
		shouldRerenderOnTransaction: false,
	});

	useEditorReadOnly(editor, readOnly);

	const getHTMLContent = useCallback(() => {
		return editor?.getHTML() || '';
	}, [editor]);

	const { sendPersistSnapshot, schedulePersistSnapshot } = useCollaborativeDocumentSync(yjsDoc, provider, getHTMLContent, saveDebounceTimeoutMs);

	// Store callback in ref to avoid effect re-runs when callback changes
	const onEditorReadyRef = useRef(onEditorReady);
	useEffect(() => {
		onEditorReadyRef.current = onEditorReady;
	}, [onEditorReady]);

	// Track if we've notified parent about the editor
	const hasNotifiedParent = useRef(false);

	// Set ready state once editor is available
	useEffect(() => {
		if (!editor || isReady) return;
		setIsReady(true);
	}, [editor, isReady]);

	// Notify parent when editor becomes ready
	useEffect(() => {
		if (!editor || !isReady) return;

		onEditorReadyRef.current?.(editor);
		hasNotifiedParent.current = true;

		// Cleanup: notify parent when component unmounts (only if we notified them)
		return () => {
			if (hasNotifiedParent.current) {
				onEditorReadyRef.current?.(null);
				hasNotifiedParent.current = false;
			}
		};
	}, [editor, isReady]);

	// Handle HTML fallback for documents created without YJS state (e.g., by nightly job)
	// When backend creates a document with only HTML content and no collaboration_state,
	// we need to hydrate the editor from the HTML content.
	useEffect(() => {
		if (!editor || !isReady || hasHydratedFromFallback.current) return;

		const htmlContent = provider.initialStatePayload?.content;
		const hasState = hasCollaborationState(provider);

		// If no collaboration_state from backend but we have HTML content, use it
		if (!hasState && htmlContent && htmlContent.trim()) {
			hasHydratedFromFallback.current = true;
			// Use queueMicrotask to avoid flushSync error when called during React render
			queueMicrotask(() => {
				// Guard against editor being destroyed before microtask runs
				if (!editor.isDestroyed) {
					editor.commands.setContent(htmlContent);
					sendPersistSnapshot();
				}
			});
		}
	}, [editor, isReady, provider, sendPersistSnapshot]);

	// Set up periodic snapshot persistence AFTER editor is ready
	useEffect(() => {
		if (!editor || readOnly || !isReady) return;

		const handleUpdate = () => {
			schedulePersistSnapshot();
		};

		editor.on('update', handleUpdate);

		return () => {
			editor.off('update', handleUpdate);
		};
	}, [editor, schedulePersistSnapshot, readOnly, isReady]);

	if (!editor) {
		return null;
	}

	return (
		<Box minH={7} sx={editorStyles} borderWidth="1px" borderColor="border.default" borderRadius="sm" p={4}>
			{!readOnly && <BubbleMenu editor={editor} />}
			<EditorContent editor={editor} />
		</Box>
	);
}

export const CoreEditor = React.memo(CoreEditorComponent);
